import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";

/**
 * Reusable sortable table-header cell. Kept module-level so React
 * doesn't warn about "unstable nested components". Parent owns the
 * ``sort`` state (`{ key, dir }`) and passes ``onSort(key)`` — the
 * canonical cycle is asc → desc → default, which the parent decides.
 *
 * Usage:
 *   const [sort, setSort] = useState({ key: "created_at", dir: "desc" });
 *   function cycleSort(k) { setSort(cur => cur.key !== k ? { key: k, dir: "asc" }
 *                                      : cur.dir === "asc" ? { key: k, dir: "desc" }
 *                                      : { key: DEFAULT_KEY, dir: "asc" }); }
 *   <SortableTh sortKey="business_name" sort={sort} onSort={cycleSort} label="Business" />
 */
export function SortableTh({ sortKey, sort, onSort, label, className = "", testidPrefix = "sort" }) {
  const active = sort?.key === sortKey;
  const Icon = !active ? ChevronsUpDown : (sort.dir === "asc" ? ChevronUp : ChevronDown);
  return (
    <th className={`text-left px-4 py-3 font-semibold ${className}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        data-testid={`${testidPrefix}-${sortKey}`}
        aria-label={`Sort by ${label}`}
        className="inline-flex items-center gap-1.5 uppercase tracking-wider text-xs hover:text-cyan-300 transition-colors"
      >
        {label}
        <Icon className={`w-3 h-3 ${active ? "text-cyan-300" : "opacity-40"}`} />
      </button>
    </th>
  );
}

/**
 * Helper: default `cycleSort` behaviour used by every table.
 * ``defaultKey`` is where "third click / clear" lands.
 */
export function makeCycleSort(setSort, defaultKey, defaultDir = "asc") {
  return (key) => setSort((cur) => {
    if (cur.key !== key) return { key, dir: "asc" };
    if (cur.dir === "asc") return { key, dir: "desc" };
    return { key: defaultKey, dir: defaultDir };
  });
}
